/**
 * Singleton for managing visibility of all assets in the asset menu.
 * Asset category, cad folder and assets must initialize themselves when component is loaded.
 *
 * todo: For now, eye-icons in asset menu are bound to functions in this class.
 * todo: This means, A LOT of calls are made during each digest cycle, depending on number of assets.
 * todo: Instead, bind to variables in the components, and broadcast message to update those variables
 * todo: when the visibility of asset/cad folder/category is changed.
 */

import { AssetManager } from './asset-manager';
import { Dxf } from './elements/dxf';
import { AssetCategory, ElementObj } from './elements/element-obj';
import { AssetWithFileInfo } from './elements/file-info/types';
import { Glb } from './elements/glb';
import { publish, SubscriptionTopic } from './messaging/pubsub';

interface State {
  categoryVisibility: { [id: string]: boolean };
  cadFolderVisibility: Map<string, boolean>;
  assetVisibility: Map<string, boolean>;
}

export class AssetVisibilityManager {
  static getInstance() {
    if (!AssetVisibilityManager.instance) {
      AssetVisibilityManager.instance = new AssetVisibilityManager();
    }

    return AssetVisibilityManager.instance;
  }
  private static instance: AssetVisibilityManager;
  private assetAutoSaveOnVisibilityChange = true;

  private assetCategoryMapping: Map<string, string> = new Map();
  private assetCadFolderMapping: Map<string, string> = new Map();

  private currentState: State = {
    categoryVisibility: {},
    cadFolderVisibility: new Map(),
    assetVisibility: new Map(),
  };
  private savedState: State | undefined;

  private constructor() {
    this.reset();
  }

  /**
   * Resets all data for this singleton.
   */
  reset() {
    // The keys must be identical to keys used in other places of the application.
    // todo: Consider replace them with an enum.
    this.currentState.categoryVisibility = {
      area: true,
      line: true,
      comment: true,
      cad: true,
      model: true,
      design: true,
      'terrain-model': true,
    };

    this.currentState.cadFolderVisibility = new Map();
    this.currentState.assetVisibility = new Map();
  }

  /**
   * Initializes the visibility of a cad folder if it has not been initialized before.
   * Returns value for current visibility.
   * @param folderId Id of CAD folder.
   * @param visible Whether cad folder is visible or not.
   */
  initCadFolder(folderId: string, visible: boolean) {
    if (!this.currentState.cadFolderVisibility.has(folderId)) {
      this.toggleCadFolder(folderId, visible);
    }
  }

  /**
   * Initializes the visibility of a cad asset if it's not been initialized before.
   * @param assetId Id of asset.
   * @param cadFolderId Id of folder.
   * @param visible Whether asset is visible by default or not.
   */
  initCadAsset(assetId: string, cadFolderId: string, visible: boolean) {
    if (this.assetCategoryMapping.has(assetId)) {
      return;
    }

    this.assetCategoryMapping.set(assetId, 'cad');
    this.assetCadFolderMapping.set(assetId, cadFolderId);

    void this.toggleAsset(assetId, visible, false);
  }

  /**
   * Initializes the visibility of an area asset if it's not been initialized before.
   * @param assetId Id of asset.
   * @param visible Whether asset is visible by default or not.
   */
  initAreaAsset(assetId: string, visible: boolean) {
    if (this.assetCategoryMapping.has(assetId)) {
      return;
    }

    this.assetCategoryMapping.set(assetId, 'area');
    void this.toggleAsset(assetId, visible, false);
  }

  /**
   * Initializes the visibility of an area asset if it's not been initialized before.
   * @param assetId Id of asset.
   * @param visible Whether asset is visible by default or not.
   */
  initDesignAsset(assetId: string, visible: boolean) {
    if (this.assetCategoryMapping.has(assetId)) {
      return;
    }

    this.assetCategoryMapping.set(assetId, 'design');
    void this.toggleAsset(assetId, visible, false);
  }

  /**
   * Initializes the visibility of a line asset if it's not been initialized before.
   * @param assetId Id of asset.
   * @param visible Whether asset is visible by default or not.
   */
  initLineAsset(assetId: string, visible: boolean) {
    if (this.assetCategoryMapping.has(assetId)) {
      return;
    }

    this.assetCategoryMapping.set(assetId, 'line');
    void this.toggleAsset(assetId, visible, false);
  }

  /**
   * Initializes the visibility of a model asset if it's not been initialized before.
   * @param assetId Id of asset.
   * @param visible Whether asset is visible by default or not.
   */
  initModelAsset(assetId: string, visible: boolean) {
    if (this.assetCategoryMapping.has(assetId)) {
      return;
    }

    this.assetCategoryMapping.set(assetId, 'model');
    void this.toggleAsset(assetId, visible, false);
  }

  /**
   * Initializes the visibility of a terrain model asset if it's not been initialized before.
   * @param assetId Id of asset.
   * @param visible Whether asset is visible by default or not.
   */
  initTerrainModelAsset(assetId: string, visible: boolean) {
    if (this.assetCategoryMapping.has(assetId)) {
      return;
    }
    this.assetCategoryMapping.set(assetId, 'terrain-model');
    void this.toggleAsset(assetId, visible, false);
  }

  /**
   * Toggles visibility for specified category.
   * All assets in category are hidden or restored to their previous visibility.
   * @param categoryType Type of category.
   * @param visible Whether the category should be shown or hidden (toggle if undefined).
   */
  toggleCategory(categoryType: AssetCategory, visible?: boolean): boolean {
    this.currentState.categoryVisibility[categoryType] =
      visible === undefined ? !this.currentState.categoryVisibility[categoryType] : visible;
    const categoryVisible = this.currentState.categoryVisibility[categoryType];

    for (const asset of AssetManager.instance.getAssetsByCategory(categoryType)) {
      if (!this.isCadAndHiddenByCadFolder(asset)) {
        if (categoryVisible) {
          this.restoreAssetVisibility(asset);
        } else {
          this.hideAsset(asset);
        }
      }
    }

    publish(SubscriptionTopic.SkyviewAssetCategoryVisibilityChanged, {
      categoryType: categoryType,
      enabled: this.currentState.categoryVisibility[categoryType],
    });

    return categoryVisible;
  }

  /**
   * Makes all categories visible.
   */
  showAllCategories() {
    for (const categoryType of Object.keys(this.currentState.categoryVisibility)) {
      this.toggleCategory(categoryType as AssetCategory, true);
    }
  }

  /**
   * Makes all CAD folders visible.
   */
  showAllCadFolders() {
    for (const folderId of this.currentState.cadFolderVisibility.keys()) {
      this.currentState.cadFolderVisibility.set(folderId, true);
    }
  }

  /**
   * Toggles visibility for specified CAD folder.
   * @param folderId Folder id.
   * @param visible Whether the CAD folder should be shown or hidden (toggle if undefined).
   */
  toggleCadFolder(folderId: string, visible?: boolean): boolean {
    // If folder visibility is undefined it's currently visible.
    this.currentState.cadFolderVisibility.set(
      folderId,
      visible !== undefined ? visible : !this.currentState.cadFolderVisibility.get(folderId),
    );

    const folderVisible = this.currentState.cadFolderVisibility.get(folderId)!;

    for (const asset of this.getCadFolderAssets(folderId)) {
      void asset.setCadFolderVisibility(folderVisible);
    }

    return folderVisible;
  }

  /**
   * Toggles visibility for a single asset.
   * @param assetUuid UUID of asset.
   * @param visible Whether the asset should be shown or hidden (toggle if undefined).
   * @param save If specified, this value will override the save-flag in the manager itself indicating whether assets should be saved on toggle.
   */
  async toggleAsset(assetUuid: string | ElementObj, visible?: boolean, save?: boolean) {
    const asset =
      assetUuid instanceof ElementObj ? assetUuid : AssetManager.instance.getAsset(assetUuid);

    if (asset) {
      await asset.toggleVisibility(visible);
      this.currentState.assetVisibility.set(asset.uuid, asset.visible);
      this.publishAssetVisibilityChanged(asset);

      // Whether to persist changes to database.
      if (save === undefined ? this.assetAutoSaveOnVisibilityChange : save) {
        // TODO: Needs to be refactored as async.
        // Or the way assets are saved needs to be changed to avoid async/await chains
        (asset as any).onChange();
      }
    }
  }

  publishAssetVisibilityChanged(asset: ElementObj) {
    publish(SubscriptionTopic.SkyviewAssetVisibilityChanged, {
      assetUuid: asset.uuid,
      visible: asset.visible,
    });
  }

  /**
   * Returns a value indicating whether the specified category is visible.
   * @param categoryType Type of category.
   */
  isCategoryVisible(categoryType: string): boolean {
    return this.currentState.categoryVisibility[categoryType];
  }

  /**
   * Returns a value indicating whether the specified cad folder is visible.
   * @param cadFolderId CAD folder id.
   */
  isCadFolderVisible(cadFolderId: string): boolean {
    const visible = this.currentState.cadFolderVisibility.get(cadFolderId);
    return visible === undefined ? true : visible;
  }

  /**
   * Returns a value indicating whether the specified asset is visible.
   * @param uuid Asset id.
   */
  isAssetVisible(uuid: string): boolean {
    return this.isAssetEnabled(uuid) && this.currentState.assetVisibility.get(uuid)!;
  }

  /**
   * Returns a value indicating whether the asset is enabled.
   * @param assetId Asset id.
   */
  isAssetEnabled(assetId: string): boolean {
    // Check if category is hidden.
    const categoryType = this.assetCategoryMapping.get(assetId)!;
    if (!this.isCategoryVisible(categoryType)) {
      return false;
    }

    // Check if asset belongs to a CAD folder that is hidden.
    if (this.assetCadFolderMapping.has(assetId)) {
      const cadFolderId = this.assetCadFolderMapping.get(assetId)!;
      if (!this.currentState.cadFolderVisibility.get(cadFolderId)) {
        return false;
      }
    }

    return true;
  }

  /**
   * Saves visibility of categories, CAD folders and assets.
   * Can be restored using `restoreState()`.
   */
  saveState() {
    // Save current state (shallow clone).
    this.savedState = {
      categoryVisibility: { ...this.currentState.categoryVisibility },
      cadFolderVisibility: new Map(this.currentState.cadFolderVisibility),
      assetVisibility: new Map(this.currentState.assetVisibility),
    };
  }

  /**
   * Restores visibility of categories, CAD folders and assets if state has been previously stored.
   */
  async restoreState() {
    if (this.savedState) {
      // Categories
      for (const categoryType of Object.keys(this.savedState.categoryVisibility)) {
        this.toggleCategory(
          categoryType as AssetCategory,
          this.savedState.categoryVisibility[categoryType],
        );
      }

      // CAD folders
      for (const [cadFolderId, visible] of this.savedState.cadFolderVisibility) {
        this.toggleCadFolder(cadFolderId, visible);
      }

      // Assets
      for (const [assetUuid, visible] of this.savedState.assetVisibility) {
        await this.toggleAsset(assetUuid, visible);
      }

      this.savedState = undefined;
    }
  }

  /**
   * Specifies whether assets should be auto saved (persisted) when the visibility changes.
   * @param autoSave Whether to enable auto saving for all assets.
   */
  setAssetAutoSaveOnVisibilityChange(autoSave: boolean) {
    this.assetAutoSaveOnVisibilityChange = autoSave;
  }

  /**
   * Returns a value that indicates whether the specified asset is a CAD asset and has a folder that is hidden.
   * @param asset Asset to check.
   */
  isCadAndHiddenByCadFolder(asset: ElementObj) {
    if (asset instanceof Dxf || asset instanceof Glb) {
      const folderId = asset.fileInfo!.folderId;
      if (this.currentState.cadFolderVisibility.get(folderId) === false) {
        return true;
      }
    }

    return false;
  }

  /**
   * Returns all CAD assets in specified folder.
   * @param folderId Id of CAD folder.
   */
  private getCadFolderAssets(folderId: string) {
    return AssetManager.instance
      .getAssetsByCategory('cad')
      .filter((cad) => (cad as AssetWithFileInfo).fileInfo!.folderId === folderId);
  }

  /**
   * Saves current visibility of asset and then hides it.
   * @param asset Asset to hide.
   */
  private hideAsset(asset: ElementObj) {
    // If category is hidden, save current visibility of asset.
    this.currentState.assetVisibility.set(asset.uuid, asset.visible);
    void asset.setCategoryVisibility(false);
  }

  /**
   * Restores visibility of specified asset.
   * @param asset Asset to restore visibility for.
   */
  private restoreAssetVisibility(asset: ElementObj) {
    if (this.currentState.assetVisibility.has(asset.uuid)) {
      void asset.setCategoryVisibility(true);
    }
  }
}
